<template>
  <div :class="$style.container">
    <form
      :class="$style.formContainer"
      enctype="multipart/form-data"
    >
      <div
        :class="[$style.dropbox, {
          [$style.dragImage]: dragging,
        }]"
        @dragenter="dragEnter"
        @dragleave="dragLeave"
        @dragover.prevent
        @drop.stop.prevent="dropImage"
      >
        <img
          v-if="!fileName && !imageUrl"
          :class="$style.fileIcon"
          :src="getUploadIcon()"
          svg-inline
        >
        <img
          v-if="imageUrl"
          :alt="fileName"
          :class="$style.previewImg"
          :src="imageUrl"
        >
        <div
          v-if="fileName"
          :class="$style.fileNameContainer"
        >
          <img
            :class="$style.fileIconInline"
            :src="getUploadIcon()"
            svg-inline
          >
          <b>{{ fileName }}</b>
        </div>
        <div>
          <label>
            {{ labelName }}
            <input
              ref="uploadInput"
              :accept="types"
              :class="$style.inputFile"
              type="file"
              @change="onFileChange"
            >
          </label>
          <p
            v-if="isEmptyUploadedFiles"
            :class="$style.inputMessage"
          >
            {{ uploadMessage }}
          </p>
        </div>
      </div>
    </form>
    <a
      :class="$style.removeLink"
      :style="{visibility: isEmptyUploadedFiles ? 'hidden' : 'visible'}"
      @click.self.prevent="handleRemoveFileClick"
    >
      remove file
    </a>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { get, isEmpty } from 'lodash';
import { notify } from 'client/util';
import Util from 'client/util/Util';

const { error } = Constants.default;

export default {
  name: 'UploadFile',

  props: {
    /**
     * Allow a custom upload icon image
     */
    customUploadIcon: {
      default: '',
      type: String,
    },

    /**
     * Allow an existing image to be specified
     */
    defaultImageUrl: {
      default: '',
      type: String,
    },

    /**
     * Signals that the file has been uploaded in order to hide the remove file link
     */
    fileUploaded: {
      default: 0,
      type: Number,
    },

    /**
     * Specifies the maxium height in pixels of an image
     */
    maxHeight: {
      default: 0,
      type: Number,
    },

    /**
     * Max size for the file
     * Value should be expressed in bytes
     * e.g. 4KB == 4096 , 8MB == 8388608
     */
    maxSize: {
      required: true,
      type: Number,
    },

    /**
     * Specifies the maxium width in pixels of an image
     */
    maxWidth: {
      default: 0,
      type: Number,
    },

    /**
     * Specifies the minimum height in pixels of an image
     */
    minHeight: {
      default: 0,
      type: Number,
    },

    /**
     * Specifies the minimum width in pixels of an image
     */
    minWidth: {
      default: 0,
      type: Number,
    },

    /**
     * Specifies the permitted type accepted
     * e.g. .jpg, .pdf, .doc, image/*, application/x-x509-ca-cert, application/msword
     * @see See [Media Types](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Unique_file_type_specifiers)
     */
    types: {
      required: true,
      type: String,
    },

    /**
     * The text for uploading the File
     * e.g. "or drag your image here", "or drag your file here"
     */
    uploadMessage: {
      default: 'or drag your file here',
      type: String,
    },
  },

  data: () => ({
    defaultUploadIcon: 'img/icons/icon-file.svg',
    dragging: false,
    fileName: '',
    imageUrl: '',
    uploadedFiles: {},
  }),

  computed: {
    isEmptyUploadedFiles() {
      return isEmpty(this.uploadedFiles);
    },

    labelName() {
      return (this.defaultImageUrl || this.fileName) ? 'upload a different file' : 'Browse';
    },
  },

  watch: {
    fileUploaded() {
      this.resetData();
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    dragEnter() {
      this.dragging = true;
    },

    dragLeave() {
      this.dragging = false;
    },

    dropImage(e) {
      const { dataTransfer: { files } } = e;

      this.dragging = false;
      this.validate(files);
    },

    getSupportedTypes(typeList) {
      return typeList.reduce((accumulator, current) => {
        if (!current.includes('image')) {
          accumulator.push(` ${current}`);
        } else {
          let type = current.split('/')[1];
          if (type.includes('+xml')) {
            type = type.substring(0, type.indexOf('+xml'));
          }
          accumulator.push(` .${type}`);
        }
        return accumulator;
      }, []);
    },

    getUploadIcon() {
      return this.customUploadIcon || this.defaultUploadIcon;
    },

    handleMounted() {
      this.resetData();
    },

    handleRemoveFileClick() {
      this.resetData();
      this.$emit('remove-file');
    },

    onFileChange(e) {
      const { target: { files } } = e;

      this.validate(files);
    },

    /**
     * This method is only called for image files
     */
    readUploadFileAsUrl(file) {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onerror = () => {
          reader.abort();
          reject(new DOMException('There was a problem parsing the input file.'));
        };

        reader.onload = async (e) => {
          try {
            resolve(e.target.result);
          } catch (err) {
            reject(err);
          }
        };

        reader.readAsDataURL(file);
      });
    },

    resetData() {
      this.$refs.uploadInput.value = '';
      this.dragging = false;
      this.fileName = '';
      this.imageUrl = get(this, 'defaultImageUrl', '');
      this.uploadedFiles = {};
      this.$emit('reset-data');
    },

    async validate(files) {
      let errMsg;
      const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml'];

      if (files.length) {
        const [file] = files;
        const typeList = this.types.split(', ');

        if (file.size > this.maxSize) {
          const { size, unit } = Util.getUnitFromSize(this.maxSize);
          errMsg = `The file size exceeds the ${size} ${unit} limit.`;
        }

        if (typeList) {
          if (!typeList.find(type => file.type.includes(type))
            && !typeList.some(type => file.name.match(type))) {
            const supportedTypes = this.getSupportedTypes(typeList);
            errMsg = `The file type is not valid. Supported types: ${supportedTypes}`;
          }
        }

        if (typeList.some(type => validImageTypes.includes(type))) {
          if (validImageTypes.includes(file.type)) {
            try {
              this.imageUrl = await this.readUploadFileAsUrl(file);
              await this.validateResolution(this.imageUrl);
            } catch (e) {
              errMsg = `The file could not be uploaded: ${e}`;
            }
          }
        }

        if (!errMsg) {
          this.fileName = file && file.name;
          this.uploadedFiles = files;
          this.$emit('upload-file', { file, imageUrl: this.imageUrl });
        } else {
          this.resetData();
          notify(error, errMsg);
        }
      }
    },

    validateResolution(file) {
      const image = new Image();
      const {
        maxHeight,
        maxWidth,
        minHeight,
        minWidth,
      } = this;

      return new Promise((resolve, reject) => {
        image.addEventListener('load', () => {
          const tooSmall = (image.naturalWidth < minWidth || image.naturalHeight < minHeight);
          const tooLarge = ((maxWidth > 0 && image.naturalWidth > maxWidth)
            || (maxHeight > 0 && image.naturalHeight > maxHeight));

          if (!tooSmall && !tooLarge) {
            resolve(file);
          } else {
            let errMsg;
            if (tooSmall) {
              errMsg = `The image resolution must be at least ${minWidth} x ${minHeight} pixels.`;
            } else {
              const heightMsg = `${maxHeight > 0 ? ` The maximum height is ${maxHeight} pixels.` : ''}`;
              const widthMsg = `${maxWidth > 0 ? ` The maximum width is ${maxWidth} pixels.` : ''}`;
              errMsg = `The image is too large.${widthMsg}${heightMsg}`;
            }
            reject(new Error(errMsg));
            this.resetData();
          }
        });
        image.src = file;
      });
    },
  },
};
</script>

<style module>
@value ( gradientLight4 ) from '~css/colors.css';
@value dropboxBackground: rgba(82, 168, 236, 0.04);
@value dropboxBorder: rgba(82, 168, 236, 0.8);

.container {
  align-self: stretch;
  flex: 1;
}

.formContainer {
  height: 100%;
}

.dropbox {
  align-items: center;
  border: 2px dashed gradientLight4;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  height: 100%;
  justify-content: center;
  position: relative;
  text-align: center;
  transition: all 0.3s linear;
}

.dropbox:hover {
  background: dropboxBackground;
  border-color: dropboxBorder;
}

.dropbox label {
  color: var(--jc-color-informational-base);
  cursor: pointer;
  font-weight: 600;
}

.dragImage {
  background: dropboxBackground;
  border-color: dropboxBorder;
}

.fileNameContainer {
  align-items: center;
  display: flex;
}

.fileIcon {
  height: 35px;
  margin: 10px 0 15px;
  pointer-events: none;
}

.fileIconInline.fileIconInline {
  margin-right: 5px;
  width: 15px;
}

.inputFile {
  display: none !important;
}

.inputMessage {
  display: inline-block;
  white-space: pre-wrap;
}

.removeLink.removeLink {
  color: black;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}

.removeLink.removeLink:hover {
  color: black;
  text-decoration: none;
}

.previewImg {
  border: inset 10px transparent !important;
  height: 150px;
  margin: 15px 0;
  object-fit: contain;
  pointer-events: none;
  width: 100%;
}
</style>
